import { Icon } from "../landing/icons";
import type { SeoPageData } from "./data";

/**
 * Renderer for a TRACE SEO content page (explainers, glossary, use-case pages).
 *
 * Pure + SSR-safe: no CSS import here (the stylesheet ships via the router, so
 * the build-time prerender can render this tree without a CSS side-effect —
 * same pattern as the landing). Everything is scoped under `[data-seo]`.
 */
export function SeoPage({ page }: { page: SeoPageData }) {
  return (
    <div data-seo data-theme="dark" lang={page.lang}>
      <header className="seo-nav">
        <div className="seo-wrap seo-nav__in">
          <a className="seo-brand" href="/" aria-label="TRACE home">
            <img src="/trace-logo.svg" alt="TRACE" width={88} height={19} />
          </a>
          <nav className="seo-nav__links">
            <a className="seo-nav__link" href="/#cue-plans">Pricing</a>
            <a className="seo-btn seo-btn--accent" href="/register">Get started</a>
          </nav>
        </div>
      </header>

      <main className="seo-wrap seo-main">
        <nav className="seo-crumb" aria-label="Breadcrumb">
          <a href="/">Home</a>
          <span aria-hidden="true">/</span>
          <span>{page.breadcrumb}</span>
        </nav>

        <article className="seo-article">
          <h1 className="seo-h1">{page.h1}</h1>
          <p className="seo-intro">{page.intro}</p>

          {page.sections.map((s, i) => (
            <section key={i} className="seo-section">
              <h2 className="seo-h2">{s.h2}</h2>
              {s.paragraphs.map((p, j) => (
                <p key={j} className="seo-p">{p}</p>
              ))}
              {s.bullets && s.bullets.length > 0 && (
                <ul className="seo-ul">
                  {s.bullets.map((b, k) => (
                    <li key={k}>{b}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {page.faq.length > 0 && (
            <section className="seo-section">
              <h2 className="seo-h2">Frequently asked</h2>
              <div className="seo-faq">
                {page.faq.map((f, i) => (
                  <details key={i} className="seo-faq__item">
                    <summary className="seo-faq__q">
                      <span>{f.q}</span>
                      <span className="seo-faq__mk" aria-hidden="true" />
                    </summary>
                    <p className="seo-faq__a">{f.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </article>

        {page.related.length > 0 && (
          <aside className="seo-related">
            <div className="seo-related__label">Keep reading</div>
            <ul>
              {page.related.map((r, i) => (
                <li key={i}>
                  <a href={r.href}>
                    <span>{r.label}</span>
                    <Icon name="arrowRight" size={15} />
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        )}

        <div className="seo-cta">
          <div className="seo-cta__title">Read every rider. Run every stage.</div>
          <a className="seo-btn seo-btn--accent seo-btn--lg" href="/register">
            Start free
            <Icon name="arrowRight" size={16} />
          </a>
        </div>
      </main>

      <footer className="seo-foot">
        <div className="seo-wrap seo-foot__in">
          <a className="seo-brand" href="/" aria-label="TRACE home">
            <img src="/trace-logo.svg" alt="TRACE" width={80} height={17} style={{ opacity: 0.7 }} />
            <span className="seo-foot__by">by Formray</span>
          </a>
          <nav className="seo-foot__links">
            <a href="/">Home</a>
            <a href="/technical-rider">Technical rider</a>
            <a href="/glossary">Glossary</a>
            <a href="/for/festival-organizers">For organizers</a>
            <a href="/#cue-plans">Pricing</a>
          </nav>
          {/* Build year (define, see build-defines.ts) — adjacent text nodes
              on purpose: they make the `<!-- -->` separators this page's
              hydration depends on visible in the prerender guard. */}
          <div className="seo-foot__cr">© {__BUILD_YEAR__} Formray</div>
        </div>
      </footer>
    </div>
  );
}

export default SeoPage;
